import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface RightDrawerProps {
  open: boolean;
  /** Small uppercase kicker above the title. */
  kicker: string;
  title: string;
  ariaLabel: string;
  testId: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * The review's one side surface, shared by the issue list and the node details.
 *
 * Portalled to `body` because the sheet sits inside a transformed motion
 * container, which would otherwise become the containing block for a fixed
 * element and mis-position the drawer.
 */
export function RightDrawer({
  open,
  kicker,
  title,
  ariaLabel,
  testId,
  onClose,
  children,
}: RightDrawerProps) {
  const { t } = useTranslation('projects');
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <aside
      className={`fixed bottom-0 right-0 top-0 z-50 flex w-[26rem] flex-col border-l border-border bg-card shadow-xl transition-transform duration-slow ease-out ${
        open ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      }`}
      data-testid={testId}
      role="dialog"
      aria-label={ariaLabel}
      aria-hidden={!open}
    >
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{kicker}</div>
          <div className="truncate text-sm font-medium text-foreground">{title}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('deliveryJobReview.rightDrawer.close')}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <XIcon className="size-4" aria-hidden />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">{children}</div>
    </aside>,
    document.body,
  );
}
